import { useState } from 'react';
import { useAuth } from '../auth/AuthProvider';
import { AppLink } from '../components/Nav';
import { GoogleIcon, XIcon } from '../components/icons';
import { LegalNotice } from '../components/LegalNotice';
import { Button, ErrorText } from '../components/ui';
import { errorMessage } from '../lib/api';

/**
 * Watching is for anyone with the link. Reacting and commenting need a
 * name the owner can answer, so the first tap from a visitor gets this:
 * Google right here, or email on the signup page and straight back.
 * Once signed in, whatever they were about to do goes ahead.
 */
export function SignInPrompt({
  purpose,
  authorName,
  onClose,
  onSignedIn,
}: {
  purpose: 'react' | 'comment';
  authorName: string | null;
  onClose: () => void;
  onSignedIn: () => void;
}) {
  const { signInWithGoogle } = useAuth();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const who = authorName?.trim() ? `${authorName.trim().split(' ')[0]}’s` : 'this';
  const next = `${window.location.pathname}${window.location.search}`;

  const google = async () => {
    setBusy(true);
    setError(null);
    try {
      await signInWithGoogle();
      onSignedIn();
    } catch (cause) {
      setError(errorMessage(cause));
      setBusy(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="sign-in-prompt-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/70 px-4"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="relative w-full max-w-sm rounded-md border border-hairline bg-slate-900 p-6 text-center shadow-raised">
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute right-3 top-3 rounded p-1 text-chalk-faint hover:text-chalk"
        >
          <XIcon size={16} />
        </button>
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-slate-800 text-[22px]">
          {purpose === 'react' ? '\u{1F44D}' : '\u{1F4AC}'}
        </div>
        <h2 id="sign-in-prompt-title" className="mt-4 text-h4 text-chalk">
          Sign up to {purpose === 'react' ? 'react to' : 'comment on'} {who} video
        </h2>
        <p className="mt-1.5 text-caption text-chalk-dim">It is always free to watch, react and comment.</p>
        <div className="mt-5 space-y-2">
          <Button variant="primary" className="w-full" busy={busy} onClick={() => void google()}>
            <GoogleIcon size={16} />
            Continue with Google
          </Button>
          <AppLink to={`/signup?next=${encodeURIComponent(next)}`} className="block">
            <Button className="w-full">Sign up with email</Button>
          </AppLink>
        </div>
        <LegalNotice className="mt-3" />
        {error ? (
          <div className="mt-3">
            <ErrorText>{error}</ErrorText>
          </div>
        ) : null}
        <p className="mt-4 text-caption text-chalk-faint">
          Already have an account?{' '}
          <AppLink to={`/login?next=${encodeURIComponent(next)}`} className="text-signal hover:underline">
            Log in
          </AppLink>
        </p>
      </div>
    </div>
  );
}
